import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import './PromptEditors.scss';

const PromptEditors = ({
  dispatcherPrompt,
  datakeeperPrompt,
  selectedModel,
  availableModels,
  promptConfigs,
  onDispatcherChange,
  onDatakeeperChange,
  onModelChange,
  onSaveConfig,
  onFetchConfigs,
  onConfigChange,
  onRestoreDefault
}) => {
  const [activeTab, setActiveTab] = useState('dispatcher');
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');

  useEffect(() => {
    onFetchConfigs(activeTab);
  }, [activeTab]);

  const handleTabChange = tab => {
    setActiveTab(tab);
    setSelectedConfigId('');
    if (onConfigChange) onConfigChange(null);
  };

  const handleConfigSelect = configId => {
    setSelectedConfigId(configId);
    if (!configId) {
      if (onConfigChange) onConfigChange(null);
      if (onRestoreDefault) onRestoreDefault();
      return;
    }
    const config = promptConfigs.find(c => String(c._id || c.id) === String(configId));
    if (!config) return;
    if (activeTab === 'dispatcher') {
      onDispatcherChange(config.prompt);
    } else {
      onDatakeeperChange(config.prompt);
    }
    if (config.model) {
      onModelChange(config.model);
    }
    if (onConfigChange) onConfigChange(config.name);
  };

  const filteredConfigs = promptConfigs.filter(c => c.type === activeTab);

  const handleSave = async () => {
    if (!saveName.trim()) return;
    const prompt = activeTab === 'dispatcher' ? dispatcherPrompt : datakeeperPrompt;
    try {
      await onSaveConfig({ type: activeTab, name: saveName.trim(), prompt, model: selectedModel });
      cogoToast.success('Config saved');
      setSaveName('');
      setSaving(false);
    } catch (error) {
      cogoToast.error(error?.message || 'Failed to save config');
    }
  };

  return (
    <div className='prompt-editors'>
      <div className='editors-container'>
        <div className='editor-tabs-row'>
          <div className='editor-tabs'>
            <button className={cn('editor-tab', { active: activeTab === 'dispatcher' })} onClick={() => handleTabChange('dispatcher')}>
              Dispatcher Prompt
            </button>
            <button className={cn('editor-tab', { active: activeTab === 'datakeeper' })} onClick={() => handleTabChange('datakeeper')}>
              Datakeeper Prompt
            </button>
            <div className='config-selector'>
              <label>Config:</label>
              <select value={selectedConfigId} onChange={e => handleConfigSelect(e.target.value)}>
                <option value=''>Current (default)</option>
                {filteredConfigs.map(c => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='model-selector'>
              <label>Model:</label>
              <select value={selectedModel} onChange={e => onModelChange(e.target.value)}>
                {availableModels.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='save-config-inline'>
              {saving ? (
                <div className='save-input-group'>
                  <input placeholder='Config name...' value={saveName} onChange={e => setSaveName(e.target.value)} autoFocus />
                  <button onClick={handleSave} disabled={!saveName.trim()}>
                    Save
                  </button>
                  <button className='cancel-btn' onClick={() => setSaving(false)}>
                    ×
                  </button>
                </div>
              ) : (
                <button className='save-btn' onClick={() => setSaving(true)}>
                  Save Config
                </button>
              )}
            </div>
          </div>
        </div>

        <div className='editor-content'>
          <textarea
            className={cn({ hidden: activeTab !== 'dispatcher' })}
            value={dispatcherPrompt}
            onChange={e => onDispatcherChange(e.target.value)}
            placeholder='Enter dispatcher system prompt...'
          />
          <textarea
            className={cn({ hidden: activeTab !== 'datakeeper' })}
            value={datakeeperPrompt}
            onChange={e => onDatakeeperChange(e.target.value)}
            placeholder='Enter datakeeper system prompt...'
          />
        </div>
      </div>
    </div>
  );
};

export default PromptEditors;
